/**
 * DraftHistory — surfaces the full draft-resubmission experience inside
 * ExaminerReport for any analysis that belongs to a multi-draft assignment
 * (or as the entry point to upload the next draft when it's still v1).
 *
 * Renders (in order):
 *   1. Version timeline strip  — "Draft 1", "Draft 2 (Current) • 30 Jul 15:18"
 *   2. Mini progress graph     — 53% → 61% → 68% …
 *   3. Achievement banner      — grade band jumped, or +N pts gained
 *   4. Compact improvement banner (v2+ only)
 *   5. Expandable per-criterion breakdown
 *   6. "What's changed" summary — resolved / improved / needs work
 *   7. Upload New Draft ↻ CTA + reassurance text
 */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  getAssignmentVersions, uploadFile, startAnalysis, errorMessage,
} from "../../lib/api";
import { gradeBand } from "../../lib/scoreUtils";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  RefreshCw, TrendingUp, TrendingDown, ChevronDown, ChevronRight,
  X, Upload, Loader2, Trophy, Check, CircleAlert, ArrowRight,
  Sparkles, Lock, ShieldCheck, ChevronLeft,
} from "lucide-react";

const CRITERIA = [
  ["assignment_fit", "Assignment Fit"],
  ["critical_thinking", "Critical Analysis"],
  ["academic_writing", "Academic Writing"],
  ["referencing", "Referencing"],
  ["writing_authenticity", "Writing Authenticity"],
  ["structure", "Structure"],
];

const fmtStamp = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch (e) { return ""; }
};

// -----------------------------------------------------------------------------
// Upload New Draft modal
// -----------------------------------------------------------------------------
const UploadNewDraftModal = ({ assignmentId, nextDraftNumber, previousDraftAgeMinutes,
                                onClose, onSubmitting }) => {
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmTooSoon, setConfirmTooSoon] = useState(false);
  const fileRef = useRef(null);
  const isTooSoon = previousDraftAgeMinutes != null && previousDraftAgeMinutes < 15;

  const chooseFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFile(file);
      setText(up.text || "");
      setFilename(file.name);
      toast.success(`Loaded "${file.name}"`);
    } catch (e) {
      toast.error(errorMessage(e, "Could not read that file."));
    } finally { setBusy(false); }
  };

  const submit = async () => {
    const words = (text.trim().match(/\b\w+\b/g) || []).length;
    if (words < 30) return toast.error("Add at least 30 words for a meaningful analysis.");
    if (isTooSoon && !confirmTooSoon) {
      setConfirmTooSoon(true);
      return;
    }
    setBusy(true);
    try {
      await onSubmitting({
        text: text.trim(),
        filename: filename || `Draft ${nextDraftNumber}.txt`,
        parent_id: assignmentId,
      });
    } catch (e) {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="upload-new-draft-modal">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <p className="font-display text-lg text-zinc-900 flex items-center gap-2">
              <RefreshCw size={18} className="text-indigo-600" /> Upload New Draft
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">This will be <b>Draft {nextDraftNumber}</b>. Your previous reports will always be preserved.</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="upload-new-draft-close"><X size={18} /></button>
        </div>

        {confirmTooSoon ? (
          <div className="p-6 space-y-4">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
              <p className="font-medium text-amber-900 mb-1 text-sm">Was that intentional?</p>
              <p className="text-xs text-amber-800">Your previous draft was uploaded less than 15 minutes ago. Uploading again now will create Draft {nextDraftNumber} — the previous one won&rsquo;t be overwritten, but you may want to double-check you meant to submit a genuinely new version.</p>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmTooSoon(false)} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900" data-testid="upload-cancel-tooso-btn">Back</button>
              <button onClick={() => { setConfirmTooSoon(false); submit(); }} className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700" data-testid="upload-confirm-tooso-btn">
                Yes, create Draft {nextDraftNumber}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3 flex items-start gap-2">
                <ShieldCheck size={14} className="text-indigo-600 mt-0.5 shrink-0" />
                <p className="text-xs text-indigo-900">Uploading a new draft creates <b>Draft {nextDraftNumber}</b>. Your previous reports will always be preserved so you can compare progress.</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.docx,.doc,.txt,.rtf,.md"
                className="hidden"
                onChange={(e) => chooseFile(e.target.files?.[0])}
                data-testid="upload-new-draft-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="upload-new-draft-choose-file"
                className="w-full rounded-lg border-2 border-dashed border-zinc-300 hover:border-indigo-400 py-5 text-sm text-zinc-600 flex flex-col items-center gap-2 transition-colors"
              >
                <Upload size={20} className="text-zinc-400" />
                <span>{filename ? `Selected: ${filename}` : "Click to upload your revised draft"}</span>
              </button>
              <div className="relative flex items-center">
                <div className="flex-1 border-t border-zinc-200" />
                <span className="px-3 text-xs text-zinc-400">or paste below</span>
                <div className="flex-1 border-t border-zinc-200" />
              </div>
              <textarea
                data-testid="upload-new-draft-text"
                className="w-full min-h-[200px] rounded-lg border border-zinc-300 p-3 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 outline-none"
                placeholder="Paste your revised draft here…"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <p className="text-xs text-zinc-500">{(text.trim().match(/\b\w+\b/g) || []).length} words</p>
            </div>
            <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900">Cancel</button>
              <button
                onClick={submit}
                disabled={busy || !text.trim()}
                data-testid="upload-new-draft-submit"
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Analyse Draft {nextDraftNumber}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Mini timeline graph
// -----------------------------------------------------------------------------
const TimelineGraph = ({ versions, currentId }) => {
  const points = versions.filter((v) => typeof v.grade === "number");
  if (points.length < 1) return null;
  const min = Math.min(...points.map((p) => p.grade), 40);
  const max = Math.max(...points.map((p) => p.grade), 80);
  const range = Math.max(10, max - min);
  return (
    <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-4" data-testid="draft-timeline-graph">
      <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wide mb-3">Grade progression</p>
      <div className="flex items-end gap-2 h-24">
        {points.map((v, i) => {
          const heightPct = 20 + ((v.grade - min) / range) * 80;
          const isCurrent = v.id === currentId;
          return (
            <React.Fragment key={v.id}>
              {i > 0 && <div className="flex-1 h-px bg-zinc-300 self-center relative -mx-2" />}
              <div className="flex flex-col items-center gap-1.5 shrink-0" data-testid={`timeline-pt-${v.version}`}>
                <span className={`font-mono text-xs tabular-nums font-semibold ${isCurrent ? "text-indigo-700" : "text-zinc-500"}`}>{v.grade}%</span>
                <div
                  className={`w-9 rounded-md transition-all ${isCurrent ? "bg-indigo-600 shadow-md" : "bg-indigo-200"}`}
                  style={{ height: `${heightPct}%` }}
                />
                <span className="text-[10px] text-zinc-500">D{v.version}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// Version pill strip
// -----------------------------------------------------------------------------
const VersionStrip = ({ versions, currentId, onSelect }) => {
  return (
    <div className="flex items-stretch gap-2 flex-wrap" data-testid="version-strip">
      {versions.map((v) => {
        const isCurrent = v.id === currentId;
        return (
          <button
            key={v.id}
            onClick={() => !isCurrent && onSelect(v.id)}
            disabled={isCurrent}
            data-testid={`version-pill-${v.version}`}
            className={`text-left rounded-xl border px-3 py-2 min-w-[130px] transition-all ${
              isCurrent
                ? "border-indigo-600 bg-indigo-600 text-white cursor-default"
                : "border-zinc-200 bg-white text-zinc-800 hover:border-indigo-300 hover:shadow-sm"
            }`}
          >
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${isCurrent ? "text-indigo-200" : "text-zinc-400"}`}>
              Draft {v.version}{isCurrent && " (Current)"}
            </p>
            <p className={`font-display text-base font-semibold tabular-nums ${isCurrent ? "text-white" : "text-zinc-900"}`}>
              {typeof v.grade === "number" ? `${v.grade}%` : "—"}
            </p>
            <p className={`text-[10px] font-mono ${isCurrent ? "text-indigo-200" : "text-zinc-500"}`}>{fmtStamp(v.created_at)}</p>
          </button>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Compact improvement banner + expandable detail
// -----------------------------------------------------------------------------
const ImprovementSummary = ({ prev, curr }) => {
  const [expanded, setExpanded] = useState(false);
  const gradeDelta = (typeof curr.grade === "number" && typeof prev.grade === "number") ? curr.grade - prev.grade : null;
  const readyDelta = (typeof curr.readiness === "number" && typeof prev.readiness === "number") ? curr.readiness - prev.readiness : null;

  const prevIssues = (prev.priority_improvements || []).map((p) => (typeof p === "string" ? p : (p.action || ""))).map((s) => s.trim()).filter(Boolean);
  const currIssues = (curr.priority_improvements || []).map((p) => (typeof p === "string" ? p : (p.action || ""))).map((s) => s.trim()).filter(Boolean);
  // Naive resolved/new — an issue is "resolved" if it was in prev but not currently listed.
  const resolved = prevIssues.filter((p) => !currIssues.some((c) => _issueMatch(c, p)));
  const introduced = currIssues.filter((c) => !prevIssues.some((p) => _issueMatch(c, p)));

  const criteriaRows = CRITERIA.map(([key, label]) => {
    const was = (prev.readiness_full || {})[key];
    const now = (curr.readiness_full || {})[key];
    if (typeof was !== "number" && typeof now !== "number") return null;
    return { key, label, was: was ?? 0, now: now ?? 0, delta: (now ?? 0) - (was ?? 0) };
  }).filter(Boolean);

  const improved = criteriaRows.filter((r) => r.delta >= 3);
  const needsWork = criteriaRows.filter((r) => r.now < 65 && r.delta < 3).sort((a, b) => a.now - b.now).slice(0, 3);

  return (
    <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5" data-testid="improvement-summary">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={16} className="text-indigo-600" />
        <p className="font-display text-base font-semibold text-zinc-900">What&rsquo;s changed since Draft {prev.version}</p>
      </div>

      {/* Compact banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricPill
          icon="📈"
          label="Grade"
          from={prev.grade} to={curr.grade}
          delta={gradeDelta}
          formatter={(v) => v == null ? "—" : `${v}%`}
          testId="metric-grade"
        />
        <MetricPill
          icon="✅"
          label="Issues resolved"
          value={resolved.length}
          tone="emerald"
          testId="metric-resolved"
        />
        <MetricPill
          icon="⚠️"
          label="New issues"
          value={introduced.length}
          tone={introduced.length > 0 ? "amber" : "neutral"}
          testId="metric-introduced"
        />
        <MetricPill
          icon="🎯"
          label="Readiness"
          from={prev.readiness} to={curr.readiness}
          delta={readyDelta}
          formatter={(v) => v == null ? "—" : `${v}%`}
          testId="metric-readiness"
        />
      </div>

      {/* Achievement banner */}
      {gradeDelta != null && Math.abs(gradeDelta) > 0 && (
        <AchievementBanner
          gradeDelta={gradeDelta}
          prevGrade={prev.grade}
          currGrade={curr.grade}
        />
      )}

      {/* What's changed prose summary */}
      {(improved.length > 0 || needsWork.length > 0) && (
        <div className="mt-4 rounded-xl bg-white border border-zinc-200 p-4" data-testid="whats-changed-summary">
          <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-2">What&rsquo;s changed</p>
          <div className="space-y-1.5">
            {improved.map((r) => (
              <p key={r.key} className="text-sm text-zinc-800 flex items-center gap-2"><Check size={14} className="text-emerald-600 shrink-0" /> <span><b>{r.label}</b> improved <span className="text-emerald-700 font-mono text-xs">+{r.delta}</span></span></p>
            ))}
            {needsWork.slice(0, 2).map((r) => (
              <p key={r.key} className="text-sm text-zinc-800 flex items-center gap-2"><CircleAlert size={14} className="text-amber-600 shrink-0" /> <span><b>{r.label}</b> still needs work <span className="text-zinc-500 font-mono text-xs">{r.now}%</span></span></p>
            ))}
          </div>
        </div>
      )}

      {/* Expand for full per-criterion breakdown */}
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid="improvement-expand"
        className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
      >
        {expanded ? "Hide detailed breakdown" : "Show detailed breakdown"} <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-xl border border-zinc-200 overflow-hidden bg-white" data-testid="criteria-breakdown">
              <div className="grid grid-cols-[1fr_60px_60px_60px] px-4 py-2 bg-zinc-50 text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">
                <span>Criterion</span>
                <span className="text-right">Prev</span>
                <span className="text-right">Now</span>
                <span className="text-right">Δ</span>
              </div>
              {criteriaRows.map((r) => (
                <div key={r.key} className="grid grid-cols-[1fr_60px_60px_60px] px-4 py-2 border-t border-zinc-100 text-sm items-center" data-testid={`criteria-row-${r.key}`}>
                  <span className="text-zinc-800">{r.label}</span>
                  <span className="font-mono text-right text-zinc-500 tabular-nums">{r.was}%</span>
                  <span className="font-mono text-right font-semibold text-zinc-900 tabular-nums">{r.now}%</span>
                  <span
                    className="font-mono text-right font-semibold tabular-nums"
                    style={{ color: r.delta > 0 ? "#059669" : r.delta < 0 ? "#DC2626" : "#9CA3AF" }}
                  >
                    {r.delta > 0 ? "+" : ""}{r.delta}
                  </span>
                </div>
              ))}
            </div>

            {resolved.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">Issues you&rsquo;ve resolved</p>
                {resolved.slice(0, 5).map((s, i) => (
                  <p key={i} className="text-sm text-zinc-800 flex items-start gap-2 mb-1"><Check size={14} className="text-emerald-600 shrink-0 mt-0.5" />{s}</p>
                ))}
              </div>
            )}
            {introduced.length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wide mb-1.5">New issues in this draft</p>
                {introduced.slice(0, 5).map((s, i) => (
                  <p key={i} className="text-sm text-zinc-800 flex items-start gap-2 mb-1"><CircleAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />{s}</p>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MetricPill = ({ icon, label, from, to, value, delta, formatter, tone, testId }) => {
  const isDelta = delta != null;
  const toneColour = tone === "emerald"
    ? "text-emerald-700"
    : tone === "amber"
    ? "text-amber-700"
    : "text-zinc-900";
  return (
    <div className="rounded-xl bg-white border border-zinc-200 px-3 py-2.5" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1">{icon} {label}</p>
      {value != null ? (
        <p className={`font-display text-lg font-semibold tabular-nums mt-0.5 ${toneColour}`}>{value}</p>
      ) : (
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span className="text-xs text-zinc-500 tabular-nums">{formatter(from)}</span>
          <ArrowRight size={11} className="text-zinc-400" />
          <span className="font-display text-lg font-semibold text-zinc-900 tabular-nums">{formatter(to)}</span>
          {isDelta && (
            <span
              className="text-xs font-mono font-semibold tabular-nums ml-auto"
              style={{ color: delta > 0 ? "#059669" : delta < 0 ? "#DC2626" : "#9CA3AF" }}
            >
              {delta > 0 ? "+" : ""}{delta}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const AchievementBanner = ({ gradeDelta, prevGrade, currGrade }) => {
  const prevBand = gradeBand(prevGrade || 0);
  const currBand = gradeBand(currGrade || 0);
  const bandJumped = prevBand !== currBand && gradeDelta > 0;
  if (gradeDelta <= 0) return null;
  return (
    <motion.div
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.15 }}
      className="mt-3 rounded-xl bg-gradient-to-r from-amber-50 to-emerald-50 border border-emerald-200 p-4"
      data-testid="achievement-banner"
    >
      <div className="flex items-center gap-3">
        <div className="rounded-full bg-white p-2 shadow-sm">
          {bandJumped ? <Trophy size={20} className="text-amber-500" /> : <Sparkles size={20} className="text-emerald-600" />}
        </div>
        <div>
          <p className="font-display text-base font-semibold text-zinc-900">
            {bandJumped
              ? `🏆 You've moved from a ${prevBand} to a ${currBand} prediction.`
              : `🎉 Great work! Your grade prediction increased by ${gradeDelta} percentage point${gradeDelta === 1 ? "" : "s"}.`}
          </p>
          <p className="text-xs text-zinc-600 mt-0.5">Keep going — every draft brings you closer to your best submission.</p>
        </div>
      </div>
    </motion.div>
  );
};

// Fuzzy issue-match: two issue strings are "the same" if their first 5 significant
// words overlap by ≥3.
function _issueMatch(a, b) {
  if (!a || !b) return false;
  if (a.trim().toLowerCase() === b.trim().toLowerCase()) return true;
  const wA = a.toLowerCase().match(/\b\w{4,}\b/g)?.slice(0, 6) || [];
  const wB = b.toLowerCase().match(/\b\w{4,}\b/g)?.slice(0, 6) || [];
  if (wA.length < 2 || wB.length < 2) return false;
  const overlap = wA.filter((x) => wB.includes(x)).length;
  return overlap >= Math.min(3, Math.min(wA.length, wB.length));
}

// -----------------------------------------------------------------------------
// Main component — wires the whole thing together
// -----------------------------------------------------------------------------
export default function DraftHistory({ analysis }) {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const assignmentId = analysis?.assignment_id || analysis?.id;
  const currentId = analysis?.id;

  useEffect(() => {
    if (!assignmentId) return;
    let cancelled = false;
    setLoading(true);
    getAssignmentVersions(assignmentId)
      .then((d) => { if (!cancelled) setVersions(d.versions || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [assignmentId]);

  const currIndex = useMemo(() => versions.findIndex((v) => v.id === currentId), [versions, currentId]);
  const prevVersion = useMemo(() => {
    if (currIndex <= 0) return null;
    // Find nearest earlier COMPLETE version with a numeric grade
    for (let i = currIndex - 1; i >= 0; i--) {
      const v = versions[i];
      if (v.status === "complete" && typeof v.grade === "number") return v;
    }
    return null;
  }, [versions, currIndex]);
  const currVersionRow = versions[currIndex] || {
    version: analysis?.version || 1,
    grade: analysis?.report?.estimated_grade?.percentage,
    readiness: analysis?.report?.readiness?.submission_ready,
    readiness_full: {
      assignment_fit: analysis?.report?.assignment_analysis?.answered_question?.score,
      critical_thinking: analysis?.report?.readiness?.critical_thinking,
      academic_writing: analysis?.report?.readiness?.academic_writing,
      referencing: analysis?.report?.readiness?.referencing,
      writing_authenticity: analysis?.report?.readiness?.writing_authenticity,
      structure: analysis?.report?.readiness?.structure,
    },
    priority_improvements: analysis?.report?.priority_improvements || [],
    id: analysis?.id, created_at: analysis?.created_at,
  };

  const nextDraftNumber = (versions[versions.length - 1]?.version || (analysis?.version || 1)) + 1;

  // Time since the LAST version was uploaded (for the 15-min guard)
  const lastCreated = versions[versions.length - 1]?.created_at || analysis?.created_at;
  const previousDraftAgeMinutes = lastCreated
    ? Math.round((Date.now() - new Date(lastCreated).getTime()) / 60000)
    : null;

  const onSubmitting = async (payload) => {
    setSubmitting(true);
    try {
      // parent_id must be the CURRENT draft's id so the new draft chains from
      // the one the user was viewing (not the root of the assignment tree).
      const { id } = await startAnalysis({ ...payload, parent_id: currentId });
      toast.success(`Draft ${nextDraftNumber} is being analysed…`);
      setShowUpload(false);
      // Navigate to the report page where the new analysis will poll
      navigate(`/report/${id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        const reason = e.response.data?.detail;
        if (reason === "pro_required_for_revision") {
          toast.message("Draft resubmission is a Pro feature.");
          navigate("/pricing");
        } else if (reason === "sign_in_required") {
          setAuthOpen(true);
        } else {
          navigate("/pricing");
        }
      } else {
        toast.error(errorMessage(e, "Could not start analysis."));
      }
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  const hasMultipleDrafts = versions.length > 1;

  return (
    <section className="mt-8 no-print" data-testid="draft-history-panel">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
          <RefreshCw size={16} className="text-indigo-600" />
        </div>
        <h2 className="font-display text-lg font-semibold text-zinc-900 tracking-tight">
          Draft history
        </h2>
        {hasMultipleDrafts && <span className="text-[10px] font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{versions.length} drafts</span>}
      </div>

      {hasMultipleDrafts && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-5">
          <VersionStrip versions={versions} currentId={currentId} onSelect={(id) => navigate(`/report/${id}`)} />
          <TimelineGraph versions={versions} currentId={currentId} />
        </div>
      )}

      {prevVersion && (
        <div className="mt-4">
          <ImprovementSummary prev={prevVersion} curr={currVersionRow} />
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-indigo-200 bg-white p-5" data-testid="upload-new-draft-cta">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="font-display text-base font-semibold text-zinc-900">Ready to improve this draft?</p>
            <p className="text-sm text-zinc-600 mt-0.5">Address the feedback above, upload your next draft, and MyAE will show you exactly what&rsquo;s improved.</p>
          </div>
          <button
            onClick={() => {
              if (!user) { setAuthOpen(true); return; }
              setShowUpload(true);
            }}
            disabled={submitting}
            data-testid="upload-new-draft-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 text-white px-5 py-2.5 text-sm font-semibold hover:bg-indigo-700 whitespace-nowrap disabled:opacity-60"
          >
            <RefreshCw size={15} /> Upload New Draft ↻
          </button>
        </div>
        <p className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1.5">
          <ShieldCheck size={11} /> This will be Draft {nextDraftNumber}. Your previous reports will always be preserved.
        </p>
      </div>

      {showUpload && (
        <UploadNewDraftModal
          assignmentId={assignmentId}
          nextDraftNumber={nextDraftNumber}
          previousDraftAgeMinutes={previousDraftAgeMinutes}
          onClose={() => setShowUpload(false)}
          onSubmitting={onSubmitting}
        />
      )}
    </section>
  );
}
